import { useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, LayoutGrid, TriangleAlert } from "lucide-react";

// Hooks
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { useMenus } from "@/hooks/menu/useMenus";

// Components
import ModuleCard from "@/components/painel/ModuleCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import {
  getMenuIconElement,
  getMenuDescription,
  APROVACAO_SUBMENU_IDS,
} from "@/components/menu/menu-catalog";
import { useAprovacoesResumo } from "@/hooks/use-aprovacoes-resumo";

// Background Grid (Reutilizável)
const DashboardBackground = () => (
  <div className="absolute inset-0 -z-10 h-full w-full overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] opacity-60 [background-size:20px_20px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[radial-gradient(#ffffff15_1px,transparent_1px)]" />
    <div className="pointer-events-none absolute left-1/2 top-0 h-[300px] w-[800px] -translate-x-1/2 rounded-full bg-primary/10 blur-[100px]" />
  </div>
);

interface SubMenuDashboardProps {
  /**
   * ID do módulo pai (ex: 'atendimento', 'compras').
   * Pode ser passado por prop ou extraído do useParams() se você criar uma rota para isso.
   */
  parentId: string;
}

const SubMenuDashboard = () => {
  const { goTo } = useNavigationHelpers();
  const { menus, isLoading, hasError } = useMenus();

  const { parentId } = useParams();

  // Encontra o menu pai e extrai os submenus baseados nas permissões que o backend já filtrou
  const parentMenu = useMemo(() => {
    return menus?.find((m) => m.id === parentId);
  }, [menus, parentId]);

  const subMenus = parentMenu?.subMenus || [];

  // Badges de aprovação: só busca (e só existe) quando este módulo tem os submenus de
  // aprovação — nos demais módulos o hook fica desligado (enabled=false), sem chamada.
  const idsAprovacao = Object.values(APROVACAO_SUBMENU_IDS) as string[];
  const temAprovacao = subMenus.some((sub) => idsAprovacao.includes(sub.id));
  const { counts } = useAprovacoesResumo({ enabled: temAprovacao });

  const handleNavigation = useCallback(
    (url: string) => {
      // Como as URLs já vêm montadas do backend (ex: '/job', '/creditaveis'),
      // basta passar para a sua função de navegação principal.
      goTo(url);
    },
    [goTo],
  );

  // Variantes do Container para Stagger
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.04 },
    },
  };

  return (
    <div className="relative min-h-screen w-full">
      <DashboardBackground />

      <div className="relative mx-auto w-full px-4 pb-4 pt-4 md:px-8">
        {/* Header Section Animado */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-12 space-y-4"
        >
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goTo("/")}
              className="text-muted-foreground transition-colors hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Módulos
            </Button>

            <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              <span>{parentMenu?.nome || "Detalhes do Módulo"}</span>
            </div>
          </div>

          <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
            Acesso a{" "}
            <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              {parentMenu?.nome || "Ferramentas"}
            </span>
          </h1>
          <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Selecione uma das funcionalidades disponíveis para este módulo.
          </p>
        </motion.div>

        {/* Grid Section */}
        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-52 space-y-4 rounded-2xl border border-border/50 bg-card/50 p-6 backdrop-blur-sm"
              >
                <div className="flex justify-between">
                  <Skeleton className="h-12 w-12 rounded-xl bg-muted" />
                </div>
                <div className="space-y-3 pt-6">
                  <Skeleton className="h-6 w-3/4 bg-muted" />
                  <Skeleton className="h-4 w-full bg-muted" />
                  <Skeleton className="h-4 w-2/3 bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {hasError ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full flex flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-card/30 p-8 py-20 text-center"
              >
                <div className="mb-4 rounded-full bg-muted p-4">
                  <TriangleAlert className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  Não foi possível carregar as funcionalidades
                </h3>
                <p className="mt-1 text-muted-foreground">
                  Verifique sua conexão e tente novamente.
                </p>
              </motion.div>
            ) : subMenus.length > 0 ? (
              subMenus.map((sub, index) => (
                <ModuleCard
                  key={sub.id}
                  index={index}
                  title={sub.nome}
                  description={getMenuDescription(sub.id, sub.nome)}
                  icon={getMenuIconElement(sub.id)}
                  href={sub.url}
                  notificationCount={counts[sub.id]}
                  onClick={() => handleNavigation(sub.url)}
                />
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full flex flex-col items-center justify-center rounded-3xl border-2 border-dashed bg-card/30 p-8 py-20 text-center"
              >
                <div className="mb-4 rounded-full bg-muted p-4">
                  <LayoutGrid className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">
                  Nenhuma funcionalidade disponível
                </h3>
                <p className="mt-1 text-muted-foreground">
                  Não há submenus configurados ou você não possui permissão.
                </p>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SubMenuDashboard;
